// Data for the command palette (CommandPalette.vue): builds the sections
// shown under the input — live Results while typing, otherwise Recent
// searches, Recently viewed and Trending — plus the Go to shortcuts, as one
// flat, keyboard-navigable list. Owns the session cache of the trending
// probe; the live search itself is useProductSearch (shared with SearchBox).
import { computed, ref, type Component, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowRight, ClipboardList, CreditCard, History, LayoutGrid, LifeBuoy, Sparkles, Tag } from 'lucide-vue-next';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { useRecentlyViewedStore } from '../stores/recently-viewed';
import { productCover } from '../utils/product-art';
import { SEARCH_MIN_CHARS, useRecentSearches, type ProductSuggestion } from './useProductSearch';

export type PaletteSectionId = 'results' | 'recent' | 'viewed' | 'trending' | 'goto';

export interface PaletteItem {
  key: string;
  section: PaletteSectionId;
  label: string;
  meta?: string | null;
  cover?: string | null;
  icon?: Component;
  priceCents?: number;
  /** Route to open; a recent search re-runs the catalog search. */
  to: string | { name: string; query: Record<string, string> };
  /** Search term this row runs: remembered when opened, removable on `recent` rows. */
  search?: string;
}

/** ⌘ on Apple devices, Ctrl elsewhere — the shortcut itself accepts both. */
export const PALETTE_MOD_KEY = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

const RECENTLY_VIEWED_SHOWN = 4;
const TRENDING_LIMIT = 5;

// Session cache: fetched on the first open, reused for the rest of the visit.
const trending = ref<ProductSuggestion[]>([]);
let trendingRequested = false;

export async function loadTrending(): Promise<void> {
  if (trendingRequested) return;
  trendingRequested = true;
  try {
    const { data } = await api.get<{ items: ProductSuggestion[] }>('/products', {
      params: { sort: 'popular', limit: TRENDING_LIMIT },
    });
    trending.value = data.items;
  } catch {
    trendingRequested = false; // try again on the next open
  }
}

function platformMeta(p: { platform: string | null }): string | null {
  return p.platform && p.platform !== 'Other' ? p.platform : null;
}

function productItem(section: PaletteSectionId, p: ProductSuggestion): PaletteItem {
  return {
    key: `${section}:${p.id}`,
    section,
    label: p.name,
    meta: platformMeta(p),
    cover: productCover(p),
    priceCents: p.ourPriceCents,
    to: `/catalog/${p.id}`,
  };
}

export function usePaletteSections(trimmed: Ref<string>, results: Ref<ProductSuggestion[]>) {
  const { t } = useI18n();
  const auth = useAuthStore();
  const viewed = useRecentlyViewedStore();
  const { recentSearches } = useRecentSearches();

  const goTo = computed<PaletteItem[]>(() => {
    const links: { key: string; label: string; icon: Component; to: string }[] = [
      { key: 'all', label: t('nav.allGames'), icon: LayoutGrid, to: '/catalog' },
      { key: 'deals', label: t('nav.deals'), icon: Tag, to: '/catalog?sort=price_asc' },
      { key: 'new', label: t('nav.newReleases'), icon: Sparkles, to: '/catalog?sort=newest' },
      { key: 'gift', label: t('nav.giftCards'), icon: CreditCard, to: '/catalog?q=card' },
      ...(auth.isAuthenticated ? [{ key: 'orders', label: t('nav.orders'), icon: ClipboardList, to: '/orders' }] : []),
      { key: 'help', label: t('nav.help'), icon: LifeBuoy, to: '/help' },
    ];
    return links.map((l) => ({ key: `goto:${l.key}`, section: 'goto', label: l.label, icon: l.icon, to: l.to }));
  });

  const searching = computed(() => trimmed.value.length >= SEARCH_MIN_CHARS);

  /** Flat list in display order; the template groups it by `section`. */
  const items = computed<PaletteItem[]>(() => {
    if (searching.value) {
      const q = trimmed.value;
      const seeAll: PaletteItem = {
        key: 'results:all',
        section: 'results',
        label: t('search.seeAll', { query: q }),
        icon: ArrowRight,
        to: { name: 'catalog', query: { q } },
        search: q,
      };
      return [
        ...results.value.map((p) => productItem('results', p)),
        seeAll,
        ...goTo.value.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())),
      ];
    }
    return [
      ...recentSearches.value.map<PaletteItem>((term) => ({
        key: `recent:${term}`,
        section: 'recent',
        label: term,
        icon: History,
        search: term,
        to: { name: 'catalog', query: { q: term } },
      })),
      ...viewed.items.slice(0, RECENTLY_VIEWED_SHOWN).map((p) => productItem('viewed', p)),
      ...trending.value.map((p) => productItem('trending', p)),
      ...goTo.value,
    ];
  });

  const sectionLabels = computed<Record<PaletteSectionId, string>>(() => ({
    results: t('search.palette.results'),
    recent: t('search.palette.recent'),
    viewed: t('search.palette.viewed'),
    trending: t('search.palette.trending'),
    goto: t('search.palette.goTo'),
  }));

  return { items, searching, sectionLabels };
}
